import React from 'react';
import { usePubStyles, PubNav, PubFooter, PubPage } from './PublicLayout';

interface Props {
  onNavigate: (page: PubPage) => void;
}

const LEADERSHIP = [
  { name: 'Margaret O. Ellison', role: 'Chief Executive Officer',   img: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&q=80' },
  { name: 'David K. Okafor',     role: 'Chief Financial Officer',   img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&q=80' },
  { name: 'Sophia L. Chen',      role: 'Chief Technology Officer',  img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&q=80' },
  { name: 'James R. Whitfield',  role: 'Chief Risk Officer',        img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&q=80' },
];

const TIMELINE = [
  { year: '1998', title: 'Founded in New York', text: 'Nationstar Bank N.A. was established by a consortium of investment bankers with a clear vision: make private banking accessible to everyone.' },
  { year: '2005', title: 'NYSE Listing & Expansion', text: 'Listed on the NYSE and expanded operations to 48 states, growing our customer base to over 500,000 accounts.' },
  { year: '2012', title: 'Digital Transformation', text: 'Launched our first award-winning mobile banking platform, earning Best Digital Bank from Banking Technology Magazine.' },
  { year: '2018', title: 'International Offices', text: 'Opened offices in London, Dubai, and Singapore. Received full FCA authorisation for European operations.' },
  { year: '2023', title: 'Nationstar 3.0', text: 'Launched our next-generation platform featuring AI-powered insights, real-time global transfers, and automated investing.' },
  { year: '2026', title: 'Today', text: 'Serving 4.2 million customers globally with $2.4 trillion in assets under management across 180+ countries.' },
];

const VALUES = [
  { icon: '🛡', title: 'Integrity First',  text: 'Every decision is guided by honesty, transparency, and full accountability to our customers and regulators.' },
  { icon: '💡', title: 'Innovation',        text: 'We invest relentlessly in technology and talent to deliver banking experiences years ahead of the industry.' },
  { icon: '🌍', title: 'Inclusion',         text: 'Banking is a right, not a privilege. Our products are designed to be accessible to everyone.' },
  { icon: '🤝', title: 'Partnership',       text: 'We treat every customer relationship as a long-term partnership — your goals become our goals.' },
  { icon: '📊', title: 'Transparency',      text: 'No hidden fees. No jargon. No surprises. Complete clarity on how your money is managed and growing.' },
  { icon: '🔐', title: 'Security',          text: 'Military-grade encryption and real-time fraud monitoring protect your wealth 24 hours a day, 365 days a year.' },
];

export default function AboutPage({ onNavigate }: Props) {
  usePubStyles();

  return (
    <div className="pub-page">
      <PubNav current="about" onNavigate={onNavigate} />

      {/* ── Hero ── */}
      <div style={{ background: 'linear-gradient(135deg,rgba(201,168,76,.05) 0%,transparent 55%)' }}>
        <div className="pub-wrap">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center', padding: '100px 0 80px' }}>
            <div>
              <div className="pub-eyebrow">◆ Our Story</div>
              <h1 className="pub-h1">Banking built on <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>trust</em>,<br />driven by purpose</h1>
              <p className="pub-p" style={{ maxWidth: 480, marginBottom: 32 }}>
                For over 25 years, Nationstar Bank has been the financial partner of choice for millions of
                individuals, families, and businesses. We combine the reliability of a century-old institution
                with the agility of a modern fintech.
              </p>
              <div style={{ display: 'flex', gap: 12 }}>
                <button className="pub-btn-gold" onClick={() => onNavigate('landing')}>Open Account</button>
                <button className="pub-btn-ghost" onClick={() => onNavigate('contact')}>Contact Us</button>
              </div>
            </div>
            <div className="pub-img" style={{ aspectRatio: '5/4' }}>
              <img
                src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=900&h=720&fit=crop&q=85"
                alt="Nationstar Bank Headquarters"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="pub-wrap">
        <div className="pub-stat-row">
          {[['$2.4T','Assets Under Management'],['4.2M+','Global Customers'],['180+','Countries Served'],['99.99%','Platform Uptime']].map(([v,l]) => (
            <div key={l} className="pub-stat-cell">
              <div className="pub-stat-val">{v}</div>
              <div className="pub-stat-lbl">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Mission ── */}
      <div className="pub-dark">
        <div className="pub-wrap pub-section">
          <div className="pub-grid-2" style={{ gap: 80 }}>
            <div>
              <div className="pub-eyebrow">Our Mission</div>
              <h2 className="pub-h2">Empowering financial <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>freedom</em> for everyone</h2>
              <p className="pub-p" style={{ marginBottom: 20 }}>
                Nationstar Bank was founded on a single belief: that every person deserves access to financial
                services that are safe, transparent, and empowering. We have built our institution around this
                principle — continuously removing the barriers between people and their financial goals.
              </p>
              <p className="pub-p">
                Today we operate at the intersection of technology and traditional banking, offering the stability
                of a federally chartered institution with the speed and convenience of a digital-first platform.
                From your first savings account to your first international wire transfer, we are with you at every step.
              </p>
            </div>
            <div className="pub-img" style={{ aspectRatio: '4/3' }}>
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop&q=85"
                alt="Banking team at work"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Values ── */}
      <div className="pub-wrap pub-section">
        <div className="pub-eyebrow">Core Values</div>
        <h2 className="pub-h2" style={{ marginBottom: 40 }}>The principles that <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>guide us</em></h2>
        <div className="pub-grid-3">
          {VALUES.map(v => (
            <div key={v.title} className="pub-card">
              <div className="pub-card-icon">{v.icon}</div>
              <div className="pub-card-title">{v.title}</div>
              <p className="pub-card-text">{v.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Timeline ── */}
      <div className="pub-dark">
        <div className="pub-wrap pub-section">
          <div className="pub-eyebrow">Our Journey</div>
          <h2 className="pub-h2" style={{ marginBottom: 48 }}>25 years of <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>milestones</em></h2>
          <div style={{ maxWidth: 640 }}>
            {TIMELINE.map((t, i) => (
              <div key={t.year} style={{ display: 'grid', gridTemplateColumns: '72px 1px 1fr', gap: '0 24px', minHeight: 80 }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, color: 'var(--gold)', textAlign: 'right', paddingTop: 2 }}>{t.year}</div>
                <div style={{ background: 'var(--border)', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', width: 10, height: 10, borderRadius: '50%', background: 'var(--gold)', border: '2px solid var(--navy2)' }} />
                </div>
                <div style={{ paddingBottom: i < TIMELINE.length - 1 ? 32 : 0, paddingLeft: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{t.title}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.65 }}>{t.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Leadership ── */}
      <div className="pub-wrap pub-section">
        <div className="pub-eyebrow">Leadership</div>
        <h2 className="pub-h2" style={{ marginBottom: 40 }}>Meet our <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>executive team</em></h2>
        <div className="pub-grid-4">
          {LEADERSHIP.map(l => (
            <div key={l.name} style={{ background: 'var(--navy2)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', transition: 'all .3s' }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(201,168,76,.35)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLDivElement).style.transform = 'none'; }}>
              <img src={l.img} alt={l.name} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
              <div style={{ padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{l.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: .5, textTransform: 'uppercase' }}>{l.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="pub-cta-banner">
        <h2>Ready to join Nationstar Bank?</h2>
        <p>Open your account in under 5 minutes. No paperwork, no branch visit required.</p>
        <div className="pub-cta-btns">
          <button className="pub-btn-gold" onClick={() => onNavigate('landing')}>Open an Account →</button>
          <button className="pub-btn-ghost" onClick={() => onNavigate('contact')}>Talk to Us</button>
        </div>
      </div>

      <PubFooter onNavigate={onNavigate} />
    </div>
  );
}
